// Design system primitives from "LAST DAY Style Guide" (ui/LAST DAY Style Guide.dc.html).

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { RARITY_COLOR, RARITY_RANK, type Rarity, type StackKind } from '@lastday/shared';
import { t } from '@/i18n';
import { clock, cx, num, rarityName } from '@/lib/format';
import { ItemIcon, StackArt } from './Art';
import { PathIcon, P } from './Icon';
import { TierTag } from './TierTag';

// ---- Buttons ---------------------------------------------------------------------------

/**
 * Crimson palette (1. 10. 2026): `primary` is the one main action of a screen (red, white text);
 * every other button is `secondary` (outline) or `ghost`. `danger` is a red action that loses
 * something (dismantle, leave), `danger-outline` its quieter form.
 */
type Variant = 'primary' | 'secondary' | 'danger' | 'danger-outline' | 'text' | 'ghost';
type Size = 'lg' | 'md' | 'sm' | 'xs';

const variantCls: Record<Variant, string> = {
  primary: 'bg-cta text-on-cta hover:bg-cta-hover active:bg-cta-hover border border-transparent',
  secondary: 'bg-transparent text-text border border-line hover:border-text hover:bg-surface',
  danger: 'bg-danger text-on-cta hover:bg-cta-hover active:bg-cta-hover border border-transparent',
  'danger-outline': 'bg-transparent text-text border border-danger hover:bg-raised',
  text: 'bg-transparent text-text hover:text-muted underline decoration-line underline-offset-4 border border-transparent',
  ghost: 'bg-surface text-text border border-border hover:border-line hover:bg-raised',
};
const sizeCls: Record<Size, string> = {
  lg: 'h-[52px] px-[22px] text-[17px]',
  md: 'h-12 px-[22px] text-[16px]',
  sm: 'h-11 px-4 text-[15px]',
  xs: 'h-9 px-3 text-[14px]',
};

export function Button({ variant = 'primary', size = 'md', full, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; full?: boolean }) {
  const disabledCls = rest.disabled ? '!bg-raised !text-muted !border-dashed !border-border hover:!bg-raised' : '';
  return (
    <button
      {...rest}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2.5 rounded-[6px] font-display font-semibold uppercase tracking-[0.06em] transition-colors',
        variantCls[variant],
        sizeCls[size],
        full && 'w-full',
        disabledCls,
        className,
      )}
    >
      {children}
    </button>
  );
}

// ---- Cards -------------------------------------------------------------------------------

export function Card({ className, children, style, onClick }: { className?: string; children: ReactNode; style?: CSSProperties; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={style} className={cx('rounded-[6px] border border-border bg-surface', className)}>
      {children}
    </div>
  );
}

/** Ruled field-journal page with a strip of tape on top. */
export function JournalPage({ className, children, margin, tape = true }: { className?: string; children: ReactNode; margin?: boolean; tape?: boolean }) {
  return <div className={cx('journal', tape && 'tape', margin && 'journal-margin', className)}>{children}</div>;
}

/** Brushed metal tag with four rivets. */
export function MetalTag({ className, children, rivets = true }: { className?: string; children: ReactNode; rivets?: boolean }) {
  return (
    <div className={cx('metal', className)}>
      {rivets &&
        ['top-2.5 left-2.5', 'top-2.5 right-2.5', 'bottom-2.5 left-2.5', 'bottom-2.5 right-2.5'].map((p) => (
          <span key={p} className={cx('absolute size-[7px] rounded-full border border-border bg-bg', p)} />
        ))}
      {children}
    </div>
  );
}

/** Section title row: "BUILDINGS ........ Scrap 52" */
export function SectionHeader({ title, right, className }: { title: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-baseline justify-between gap-3', className)}>
      <span className="display text-[18px] font-semibold">{title}</span>
      {right != null && <span className="mono text-[12px] text-muted">{right}</span>}
    </div>
  );
}

// ---- Rarity -----------------------------------------------------------------------------------

export function RarityChip({ rarity, className, outline, short }: { rarity: Rarity; className?: string; outline?: boolean; short?: boolean }) {
  const c = RARITY_COLOR[rarity];
  return (
    <span
      className={cx('inline-flex items-center rounded-[3px] px-[7px] py-[1px] font-display text-[12px] font-semibold uppercase tracking-[0.08em]', className)}
      style={outline ? { border: `1px solid ${c}`, color: 'var(--color-text)' } : { background: c, color: 'var(--color-bg)' }}
    >
      {short ? rarityName(rarity).slice(0, 3) : rarityName(rarity)}
    </span>
  );
}

export function rarityGlow(r: Rarity, strength = 16): string | undefined {
  return RARITY_RANK[r] >= 1 ? `0 0 ${strength + RARITY_RANK[r] * 2}px -8px ${RARITY_COLOR[r]}` : undefined;
}

// ---- Danger pips ---------------------------------------------------------------------------------

export function DangerPips({ level, size = 'md', className }: { level: number; size?: 'xs' | 'sm' | 'md' | 'lg'; className?: string }) {
  // 3. 10. 2026: `xs` for the chips of the phone's map (name and danger in one line).
  const s = size === 'xs' ? 'w-[6px] h-[6px]' : size === 'sm' ? 'w-[9px] h-[6px]' : size === 'lg' ? 'w-[14px] h-[10px]' : 'w-[12px] h-[8px]';
  return (
    <span className={cx('inline-flex', size === 'xs' ? 'gap-[2px]' : 'gap-[3px]', className)} role="img" aria-label={`Danger ${level} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={cx(s, 'skew-x-[-20deg] rounded-[1px]', i <= level ? 'bg-danger' : 'border border-border bg-raised')} />
      ))}
    </span>
  );
}

// ---- Bars -------------------------------------------------------------------------------------------

export function Bar({ value, max = 1, color = 'var(--color-fill)', height = 3, className, track = 'var(--color-raised)' }: { value: number; max?: number; color?: string; height?: number; className?: string; track?: string }) {
  const w = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={cx('overflow-hidden rounded-[2px]', className)} style={{ height, background: track }}>
      <div className="h-full transition-[width] duration-500" style={{ width: `${w}%`, background: color }} />
    </div>
  );
}

/**
 * HP (Crimson, 1. 10. 2026): the bar is light grey and the HP that is gone is the red part of the
 * track (loss). Always next to the HP number.
 */
export function HpBar({ hp, max, height = 4, className }: { hp: number; max: number; height?: number; className?: string }) {
  return <Bar value={hp} max={max} height={height} color="var(--color-fill)" track="var(--color-danger)" className={className} />;
}

/** Label + number + thin bar. */
export function StatBar({ label, value, max, color, suffix, highlight }: { label: string; value: number; max: number; color?: string; suffix?: ReactNode; highlight?: boolean }) {
  return (
    <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3">
      <span className="font-display text-[15px] font-medium tracking-[0.06em] text-muted">{label}</span>
      <Bar value={value} max={max} color={color ?? (highlight ? 'var(--color-text)' : 'var(--color-fill)')} />
      <span className={cx('mono text-right text-[15px] font-medium', highlight && 'font-bold text-text')}>
        {num(value)}
        {suffix}
      </span>
    </div>
  );
}

// ---- Stamps and badges ---------------------------------------------------------------------------------

/**
 * Amounts are always `have / need`. A shortfall is not danger (Crimson, 1. 10. 2026): its number
 * gets a light frame instead of red (`data-short`).
 */
export function HaveNeed({ have, need, className, needClass = 'text-muted' }: { have: number; need: number; className?: string; needClass?: string }) {
  const short = have < need;
  return (
    <span className={cx('mono whitespace-nowrap', className)} aria-label={t('common.haveNeed', { have: num(have), need: num(need) })}>
      <span data-short={short || undefined} className={cx('font-bold text-text', short && 'rounded-[2px] px-[3px] ring-1 ring-text')}>{num(have)}</span>
      <span className={needClass}> / {num(need)}</span>
    </span>
  );
}

export function Stamp({ children, color = 'var(--color-text)', rotate = -5, className, filled }: { children: ReactNode; color?: string; rotate?: number; className?: string; filled?: boolean }) {
  return (
    <span
      className={cx('inline-block whitespace-nowrap rounded-[3px] border-2 px-2.5 py-[3px] font-display text-[15px] font-bold uppercase tracking-[0.12em]', className)}
      style={{ transform: `rotate(${rotate}deg)`, borderColor: color, color: filled ? 'var(--color-text)' : color, background: filled ? `color-mix(in srgb, ${color} 14%, transparent)` : undefined }}
    >
      {children}
    </span>
  );
}

export function NewBadge({ className, text }: { className?: string; text?: string }) {
  return (
    <span className={cx('absolute -right-1.5 -top-2 z-[2] rotate-[8deg] rounded-[2px] bg-text px-1.5 py-[1px] font-display text-[12px] font-bold tracking-[0.1em] text-bg', className)}>
      {text ?? t('common.new')}
    </span>
  );
}

export function Tag({ children, color = 'var(--color-muted)', className }: { children: ReactNode; color?: string; className?: string }) {
  return (
    <span className={cx('inline-flex items-center rounded-[3px] border px-[7px] py-[1px] font-display text-[12px] font-semibold uppercase tracking-[0.1em]', className)} style={{ borderColor: color, color }}>
      {children}
    </span>
  );
}

// ---- Item tiles ----------------------------------------------------------------------------------------

/** Loot / inventory tile: rarity border, art, qty badge, NEW stamp, optional durability. */
export function ItemTile({
  templateId,
  stack,
  rarity,
  name,
  qty,
  isNew,
  lost,
  durability,
  equipped,
  selected,
  onClick,
  showName = true,
  showRarity = true,
  className,
}: {
  templateId?: string;
  stack?: StackKind;
  /** Null for a stack: a plain grey frame and no rarity chip (rarity colours are for items, 1. 10. 2026). */
  rarity: Rarity | null;
  name?: string;
  qty?: number;
  isNew?: boolean;
  lost?: boolean;
  durability?: number | null;
  equipped?: boolean;
  selected?: boolean;
  onClick?: () => void;
  showName?: boolean;
  showRarity?: boolean;
  className?: string;
}) {
  const c = rarity ? RARITY_COLOR[rarity] : selected ? 'var(--color-text)' : 'var(--color-border)';
  const Tag_ = onClick ? 'button' : 'div';
  return (
    <Tag_
      onClick={onClick}
      className={cx('relative flex flex-col gap-2 rounded-[6px] bg-surface p-2 text-left', rarity === 'legendary' && !lost && 'shimmer legendary-frame', className)}
      style={{ border: lost ? '1px dashed var(--color-danger)' : `${selected ? 2 : 1}px solid ${c}`, boxShadow: lost || !rarity ? undefined : rarityGlow(rarity), background: lost ? 'var(--color-bg)' : selected ? 'var(--color-raised)' : undefined }}
    >
      {isNew && !lost && <NewBadge />}
      {lost && <NewBadge className="!bg-danger !text-on-cta" text="LOST" />}
      <div className={cx('hatch relative flex aspect-square items-center justify-center overflow-hidden rounded-[4px] border border-border', lost && 'opacity-60')}>
        {templateId && rarity ? <ItemIcon templateId={templateId} rarity={rarity} res={128} lazy className="!size-[82%]" /> : stack ? <StackArt kind={stack} className="size-[78%]" /> : null}
        {qty != null && qty > 1 && <span className="mono absolute bottom-1 right-1 rounded-[3px] border border-border bg-bg px-[5px] text-[13px] font-bold">×{qty}</span>}
        {equipped && <span className="absolute left-1 top-1 rounded-[3px] bg-bg px-[5px] font-display text-[11px] font-semibold tracking-[0.08em] text-text">EQ</span>}
        {/* Krug 5 (K12.1): the tier of gear, in the free corner (qty is bottom right, EQ top left). */}
        {templateId && !lost && (
          <span className="absolute bottom-1 left-1 rounded-[3px] border border-border bg-bg px-[5px] text-[12px] font-bold">
            <TierTag templateId={templateId} />
          </span>
        )}
      </div>
      {durability != null && <Bar value={durability} max={100} height={3} color={durability > 25 ? 'var(--color-fill)' : 'var(--color-danger)'} />}
      {showName && name && <span className={cx('display text-[15px] font-semibold leading-tight', lost && 'text-muted line-through')}>{name}</span>}
      {showRarity && rarity && <RarityChip rarity={rarity} outline={lost} className="self-start" />}
    </Tag_>
  );
}

// ---- Tabs, segmented controls, chips --------------------------------------------------------------------

export function Tabs<T extends string>({ value, onChange, items, className }: { value: T; onChange: (v: T) => void; items: { id: T; label: ReactNode; count?: ReactNode; guide?: string }[]; className?: string }) {
  return (
    <div className={cx('no-scrollbar flex gap-1 overflow-x-auto border-b border-border', className)} role="tablist">
      {items.map((it) => {
        const on = it.id === value;
        return (
          <button
            key={it.id}
            role="tab"
            aria-selected={on}
            data-guide={it.guide}
            onClick={() => onChange(it.id)}
            className={cx('relative flex min-h-11 shrink-0 items-center gap-2 px-3 font-display text-[15px] font-semibold uppercase tracking-[0.06em]', on ? 'text-text' : 'text-muted hover:text-text')}
          >
            {it.label}
            {it.count != null && <span className="mono text-[12px] font-normal text-muted">{it.count}</span>}
            <span className="absolute inset-x-2 bottom-[-1px] h-[2px] rounded-[1px]" style={{ background: on ? 'var(--color-text)' : 'transparent' }} />
          </button>
        );
      })}
    </div>
  );
}

// 9. 10. 2026: `label` names the group, the picked button is `aria-pressed`, and a button never submits a form it is in.
export function Segmented<T extends string>({ value, onChange, items, className, label }: { value: T; onChange: (v: T) => void; items: { id: T; label: ReactNode }[]; className?: string; label?: string }) {
  return (
    <div role="group" aria-label={label} className={cx('grid gap-[3px] rounded-[6px] border border-border bg-bg p-[3px]', className)} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((it) => {
        const on = it.id === value;
        return (
          <button
            key={it.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(it.id)}
            className={cx('min-h-10 rounded-[4px] px-2 font-display text-[14px] font-semibold uppercase tracking-[0.06em]', on ? 'bg-raised text-text shadow-[inset_0_0_0_1px_var(--color-line)]' : 'text-muted hover:bg-surface hover:text-text')}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/** A filter chip; `color` (rarity) fills it when on, otherwise the on chip is raised with a light edge. */
export function Chip({ on, onClick, children, color, className }: { on?: boolean; onClick?: () => void; children: ReactNode; color?: string; className?: string }) {
  return (
    <button
      onClick={onClick}
      className={cx('inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium', className)}
      style={on ? (color ? { background: color, borderColor: color, color: 'var(--color-bg)' } : { background: 'var(--color-raised)', borderColor: 'var(--color-text)', color: 'var(--color-text)' }) : { borderColor: 'var(--color-line)', color: 'var(--color-muted)' }}
    >
      {children}
    </button>
  );
}

// ---- States ---------------------------------------------------------------------------------------------------

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return <span className={cx('inline-block rounded-full border-2 border-border border-t-text', className)} style={{ width: size, height: size, animation: 'ld-spin 0.8s linear infinite' }} aria-label="Loading" />;
}

export function Loading({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={cx('flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted', className)}>
      <Spinner size={26} />
      <span className="mono text-[12px] tracking-[0.08em]">{label ?? t('common.loading')}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry, className }: { message?: string; onRetry?: () => void; className?: string }) {
  return (
    <div className={cx('flex min-h-[30vh] flex-col items-center justify-center gap-3 px-6 text-center', className)}>
      <span className="text-danger-text">
        <PathIcon d={P.warn} size={28} />
      </span>
      <span className="max-w-[320px] text-[15px] leading-[22px] text-muted">{message ?? t('common.error')}</span>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ title, body, action, className, icon }: { title: string; body?: string; action?: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <div className={cx('flex flex-col items-center justify-center gap-2 rounded-[6px] border border-dashed border-border px-6 py-10 text-center', className)}>
      {icon && <span className="mb-1 text-muted">{icon}</span>}
      <span className="display text-[18px] font-semibold">{title}</span>
      {body && <span className="max-w-[340px] text-[14px] leading-[20px] text-muted">{body}</span>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/**
 * An error: a form that cannot go, a failed action, a login link that expired or failed. Red text
 * with the warning icon (owner's decision on the Crimson palette, 1. 10. 2026).
 */
export function FormError({ children, id, className, testId }: { children: ReactNode; id?: string; className?: string; testId?: string }) {
  return (
    <span id={id} role="alert" data-testid={testId} data-error="" className={cx('flex items-start gap-1.5 text-[13px] leading-[18px] text-danger-text', className)}>
      <PathIcon d={P.warn} size={15} className="mt-px shrink-0" />
      <span>{children}</span>
    </span>
  );
}

/**
 * Storage at or over capacity (krug 3, S8); krug 5 (K4.4): loot that does not fit is left behind. 23. 10. 2026 (owner,
 * pilot report): `tobias` (he is at camp): he buys it for a little Scrap.
 */
export function StorageFullNote({ className, tobias = false }: { className?: string; tobias?: boolean }) {
  return (
    <div role="status" className={cx('flex items-start gap-2.5 rounded-[6px] border border-danger bg-surface px-3 py-2.5 text-[14px] leading-5', className)}>
      <span className="mt-0.5 shrink-0 text-danger-text">
        <PathIcon d={P.warn} size={18} />
      </span>
      <span>{t(tobias ? 'common.storageFullTobias' : 'common.storageFull')}</span>
    </div>
  );
}

/** Large mono timer with a thin progress bar. */
export function TimerBlock({ label, ms, progress, className, size = 40 }: { label?: string; ms: number; progress?: number; className?: string; size?: number }) {
  const text = clock(ms);
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      {label && <span className="label">{label}</span>}
      <span className="mono font-medium leading-none" style={{ fontSize: size }}>
        {text}
      </span>
      {progress != null && <Bar value={progress} max={1} className="mt-1" />}
    </div>
  );
}
